import axios, { InternalAxiosRequestConfig } from 'axios';
import { trackAuthenticationFailure } from './authentication-telemetry';
import { acquireTokenSilent } from './msal';
import { protectedResources } from './msalConfig';
import { ROUTES } from '@/constants';

/**
 * Shared Axios client for authenticated API traffic.
 *
 * Design intent:
 * - Keep auth header behavior centralized so feature code does not repeat token logic.
 * - Provide one place to configure API base URL and default request headers.
 * - Ensure all requests have consistent error handling for common auth states.
 * - Bound every request with a default timeout so a hung backend cannot leave a
 *   request pending forever (Axios defaults to `0`, i.e. no timeout).
 *
 * Base URL resolution order:
 * 1) `NEXT_PUBLIC_APP_API_URL`
 * 2) `NEXT_PUBLIC_VITE_BACKEND_URL` (fallback)
 *
 * Notes:
 * - This module only configures Axios; it does not perform routing/navigation.
 * - Token retrieval is delegated to `acquireTokenSilent()` from the MSAL layer.
 */

/**
 * Default per-request timeout (ms).
 *
 * Kept below the backend's gunicorn worker `timeout = 30` so the client surfaces a
 * clear timeout error instead of waiting indefinitely on a backend that accepts the
 * connection but never responds. Individual calls can still override this via a
 * per-request `{ timeout }` config when an endpoint legitimately needs longer.
 */
const DEFAULT_REQUEST_TIMEOUT_MS = 20_000;

/**
 * Latches once a `/logout` redirect is underway so a burst of concurrent 401s
 * cannot each queue their own navigation.
 */
let isRedirectingToLogout = false;

export const authenticatedAxios = axios.create({
  // Primary API endpoint for app data.
  // The fallback preserves compatibility with environments that still use Vite-era naming.
  baseURL: process.env.NEXT_PUBLIC_APP_API_URL || process.env.NEXT_PUBLIC_VITE_BACKEND_URL,
  // Fail a stalled request rather than hang forever (Axios default is 0 = no timeout).
  timeout: DEFAULT_REQUEST_TIMEOUT_MS,
  headers: {
    // Default payload format for typical JSON API calls.
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor: attach access tokens before outbound requests.
 *
 * Behavior:
 * - Attempts silent token acquisition for each request.
 * - If a token is available, sets `Authorization: Bearer <token>`.
 * - If token acquisition fails, logs and allows request to continue.
 *
 * Why continue on error?
 * - Avoids masking underlying API behavior and keeps client resilient.
 * - Backend can still return authoritative auth status (`401`/`403`).
 */
authenticatedAxios.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // ================================
      // Optional unauthenticated route bypass
      // ================================
      // Skip token for unauthenticated endpoints if needed
      // if (config.url?.includes('/public/')) {
      //   return config;
      // }

      // ================================
      // Silent token acquisition
      // ================================
      // Acquire access token silently from MSAL
      const token = await acquireTokenSilent(protectedResources.backendApi.scopes);

      // Add bearer token only when both token and headers container are available.
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      // Log token acquisition failures for diagnostics/observability.
      console.error('Error acquiring token for API request:', error);

      // Continue with request even if token acquisition fails
      // The backend should handle unauthorized requests appropriately
    }

    // Always return config so Axios can proceed with dispatch.
    return config;
  },
  (error) => {
    // Pass through request setup/interceptor errors unchanged.
    return Promise.reject(error);
  },
);

/**
 * Response interceptor: handle common auth-related HTTP failures.
 *
 * Behavior:
 * - Success responses are returned unchanged.
 * - On `401`, logs unauthorized state (expired/invalid token scenarios).
 * - On `403`, logs insufficient permission state.
 * - Re-throws error so caller-level handling remains intact.
 *
 * This interceptor intentionally does not force redirects/retry logic by default,
 * but includes commented guidance for where re-auth behavior could be inserted.
 */
authenticatedAxios.interceptors.response.use(
  (response) => {
    // Successful response passthrough.
    return response;
  },
  (error) => {
    // 401 => authentication problem (token missing/expired/invalid).
    if (error.response?.status === 401) {
      console.error('Unauthorized request - token may be expired, redirecting to login');

      // Clear stale identity so the localStorage fallback in AuthProvider does
      // not immediately restore an unauthenticated user on the next load.
      localStorage.removeItem('user');
      localStorage.removeItem('authenticated');

      // An unauthenticated user is SUPPOSED to be sitting on an `/auth` route,
      // so redirecting from there would reload the login page through `/logout`
      // forever. Mirrors the same guard in AuthProvider.
      const isOnAuthRoute = window.location.pathname.startsWith('/auth');

      if (!isRedirectingToLogout && !isOnAuthRoute) {
        isRedirectingToLogout = true;

        trackAuthenticationFailure({
          stage: 'backend_authorization',
          reasonCode: 'backend_unauthorized',
          flushBeforeNavigation: true,
        });

        // Hard redirect forces a full MSAL re-authentication cycle.
        // Cannot use Next.js router here (outside React tree).
        //
        // Route through `/logout` rather than straight to `/auth/login`: that
        // endpoint responds with `Clear-Site-Data` (see src/app/logout/route.ts),
        // which wipes the MSAL token/account cache in localStorage. Redirecting
        // directly to the login page left a stale/expired MSAL account in
        // place, so the next page load kept rediscovering it, silently trusting
        // it again, and hitting this same 401 - producing an infinite redirect
        // loop between the protected route and the login page.
        window.location.href = ROUTES.LOGOUT;
      }
      // 403 => authenticated but not authorized for resource/action.
    } else if (error.response?.status === 403) {
      // Forbidden - user doesn't have permission
      console.error('Forbidden - insufficient permissions');
    }

    // Preserve original error for upstream handlers (hooks, services, UI layers).
    return Promise.reject(error);
  },
);

// Default export for convenience where named import is not preferred.
export default authenticatedAxios;
